import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { FormInput } from "../../../component/shared/formElements/formInput/FormInput";
import { FormCalendar } from "../../../component/shared/formElements/formCalendar/FormCalendar";
import { FormCheckboxChips } from "../../../component/shared/formElements/formCheckBoxChip/FormCheckBoxChip";
import { FormTextarea } from "../../../component/shared/formElements/formTextarea/FormTextarea";
import { FormRichText } from "../../../component/shared/formElements/formRichText/FormRichtext";
import { FormImagePreviewAndUpload } from "../../../component/shared/formElements/FormImagePreviewAndUpload/FormImagePreviewAndUpload";

import classes from "./blogFormPage.module.css";
import { useAddBlogMutation } from "../../../services/blog/blogMutationAPiSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { ddMMyyyyToSeconds } from "../../../utils/dateFormatter";

const emptyDefaults = {
  title: "",
  category: "",
  publishing_date: "",
  slug: "",
  seo_title: "",
  seo_description: "",
  status: "",
  description: "",
  banner_image_url: "",
  is_featured: null,
  short_desc: "",
};

export const BlogAddFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [addBlog, { isLoading: isSubmitting }] = useAddBlogMutation();

  const restoredData = location.state?.blogData;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { isValid, errors },
  } = useForm({
    defaultValues: restoredData || emptyDefaults,
    mode: "onChange",
  });

  /* ---------- ReactQuill manual register ---------- */
  useEffect(() => {
    register("description", {
      required: "Blog description is required",
      validate: (val) => {
        const text = new DOMParser()
          .parseFromString(val || "", "text/html")
          .body.textContent.trim();
        return text.length > 0 || "Blog description is required";
      },
    });
  }, [register]);

  const bannerUrl = watch("banner_image_url");

  /* ---------- PREVIEW ---------- */
  const handlePreview = () => {
    navigate("/blogs/add/preview", { state: { blogData: getValues() } });
  };

  /* ---------- SUBMIT ---------- */
  const onSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        publishing_date: values.publishing_date
          ? ddMMyyyyToSeconds(values.publishing_date)
          : null,
      };

      const response = await addBlog(payload).unwrap();
      toast.success(
        response?.message?.displayMessage || "Blog Created Successfully",
      );
      navigate("/blogs");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.data?.message?.displayMessage || "Failed to create blog",
      );
    }
  };

  return (
    <div className={classes.box}>
      <h1 className={classes.box__title}>Create Blog</h1>

      <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        {/* -------------------- BASIC INFO -------------------- */}
        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Blog Information</h3>

          <div className={classes.grid}>
            <FormInput
              id="title"
              label="Title"
              register={register}
              validation={{ required: "Title is required" }}
              error={errors.title?.message}
              required
            />

            <FormInput
              id="category"
              label="Category"
              register={register}
              validation={{ required: "Category is required" }}
              error={errors.category?.message}
              required
            />

            <FormCalendar
              id="publishing_date"
              label="Publishing Date"
              watch={watch}
              setValue={setValue}
              register={register}
              required
              error={errors.publishing_date?.message}
            />

            <FormInput
              id="slug"
              label="Slug"
              register={register}
              validation={{ required: "Slug is required" }}
              error={errors.slug?.message}
              required
            />

            <FormInput
              id="seo_title"
              label="SEO Title"
              register={register}
              validation={{ required: "SEO Title is required" }}
              error={errors.seo_title?.message}
              required
            />

            <FormCheckboxChips
              id="status"
              label="Status"
              options={[
                { id: "Published", label: "Published" },
                { id: "Draft", label: "Draft" },
              ]}
              watch={watch}
              setValue={setValue}
              register={register}
              validation={{ required: "Status is required" }}
              error={errors.status?.message}
              required
            />
            <FormCheckboxChips
              id="is_featured"
              label="Blog Type"
              options={[
                { id: 0, label: "Normal" },
                { id: 1, label: "Featured" },
              ]}
              watch={watch}
              setValue={setValue}
              register={register}
              validation={{ required: "Blog Type is required" }}
              error={errors.is_featured?.message}
              required
            />
          </div>

          <FormTextarea
            id="seo_description"
            label="SEO Description"
            register={register}
            validation={{ required: "SEO Description is required" }}
            error={errors.seo_description?.message}
            required
          />
          <FormTextarea
            id="short_desc"
            label="Short Description"
            register={register}
            validation={{ required: "Short Description is required" }}
            error={errors.short_desc?.message}
            required
          />
        </div>

        {/* -------------------- DESCRIPTION -------------------- */}
        <div className={classes.section}>
          <FormRichText
            id="description"
            label="Blog Description"
            value={watch("description")}
            onChange={(val) =>
              setValue("description", val, { shouldValidate: true })
            }
            required
            error={errors.description?.message}
          />
        </div>

        {/* -------------------- BANNER IMAGE -------------------- */}
        <div className={classes.section}>
          <h3 className={classes.sectionTitle}>Banner Image</h3>

          <FormImagePreviewAndUpload
            id="banner_image_url"
            url={bannerUrl}
            required
            register={register}
            setValue={setValue}
            validation={{ required: "Banner image is required" }}
            error={errors.banner_image_url?.message}
          />
        </div>

        {/* -------------------- ACTION -------------------- */}
        <div className={classes.actions}>
          <button
            type="button"
            className={classes.previewBtn}
            onClick={handlePreview}
          >
            Preview
          </button>
          <button
            type="submit"
            className={classes.submitBtn}
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? "Creating..." : "Create Blog"}
          </button>
        </div>
      </form>
    </div>
  );
};
